/**
 * Chess Sound Effects using Web Audio API
 * Similar to Lichess sounds
 */

import { useState, useEffect } from 'react';

class ChessSounds {
    private audioContext: AudioContext | null = null;
    private enabled: boolean = true;

    constructor() {
        // Initialize AudioContext on first user interaction
        if (typeof window !== 'undefined' && 'AudioContext' in window) {
            this.audioContext = new AudioContext();
        }
    }

    private ensureAudioContext() {
        if (!this.audioContext && typeof window !== 'undefined') {
            this.audioContext = new AudioContext();
        }
        // Resume context if suspended (required for some browsers)
        if (this.audioContext && this.audioContext.state === 'suspended') {
            this.audioContext.resume();
        }
    }

    setEnabled(enabled: boolean) {
        this.enabled = enabled;
    }

    /**
     * Play move sound (normal piece move)
     */
    playMove() {
        if (!this.enabled) return;
        this.ensureAudioContext();
        if (!this.audioContext) return;

        const duration = 0.08;
        const oscillator = this.audioContext.createOscillator();
        const gainNode = this.audioContext.createGain();

        oscillator.connect(gainNode);
        gainNode.connect(this.audioContext.destination);

        oscillator.frequency.value = 220; // A3
        oscillator.type = 'sine';

        gainNode.gain.setValueAtTime(0.3, this.audioContext.currentTime);
        gainNode.gain.exponentialRampToValueAtTime(0.01, this.audioContext.currentTime + duration);

        oscillator.start(this.audioContext.currentTime);
        oscillator.stop(this.audioContext.currentTime + duration);
    }

    /**
     * Play capture sound (capturing a piece)
     */
    playCapture() {
        if (!this.enabled) return;
        this.ensureAudioContext();
        if (!this.audioContext) return;

        const duration = 0.12;
        const oscillator = this.audioContext.createOscillator();
        const gainNode = this.audioContext.createGain();

        oscillator.connect(gainNode);
        gainNode.connect(this.audioContext.destination);

        oscillator.frequency.value = 330; // E4
        oscillator.type = 'triangle';

        gainNode.gain.setValueAtTime(0.4, this.audioContext.currentTime);
        gainNode.gain.exponentialRampToValueAtTime(0.01, this.audioContext.currentTime + duration);

        oscillator.start(this.audioContext.currentTime);
        oscillator.stop(this.audioContext.currentTime + duration);
    }

    /**
     * Play correct move sound (puzzle solved correctly)
     */
    playCorrect() {
        if (!this.enabled) return;
        this.ensureAudioContext();
        if (!this.audioContext) return;

        const duration = 0.3;
        
        // Play two tones for a pleasant "ding"
        [440, 554.37].forEach((freq, index) => {
            const oscillator = this.audioContext!.createOscillator();
            const gainNode = this.audioContext!.createGain();

            oscillator.connect(gainNode);
            gainNode.connect(this.audioContext!.destination);

            oscillator.frequency.value = freq; // A4 and C#5
            oscillator.type = 'sine';

            const startTime = this.audioContext!.currentTime + (index * 0.1);
            gainNode.gain.setValueAtTime(0.25, startTime);
            gainNode.gain.exponentialRampToValueAtTime(0.01, startTime + duration);

            oscillator.start(startTime);
            oscillator.stop(startTime + duration);
        });
    }

    /**
     * Play wrong move sound (incorrect puzzle move)
     */
    playWrong() {
        if (!this.enabled) return;
        this.ensureAudioContext();
        if (!this.audioContext) return;

        const duration = 0.2;
        const oscillator = this.audioContext.createOscillator();
        const gainNode = this.audioContext.createGain();

        oscillator.connect(gainNode);
        gainNode.connect(this.audioContext.destination);

        oscillator.frequency.value = 130; // C3 (low, unpleasant)
        oscillator.type = 'sawtooth';

        gainNode.gain.setValueAtTime(0.25, this.audioContext.currentTime);
        gainNode.gain.exponentialRampToValueAtTime(0.01, this.audioContext.currentTime + duration);

        oscillator.start(this.audioContext.currentTime);
        oscillator.stop(this.audioContext.currentTime + duration);
    }

    /**
     * Play puzzle complete sound (puzzle finished successfully)
     */
    playPuzzleComplete() {
        if (!this.enabled) return;
        this.ensureAudioContext();
        if (!this.audioContext) return;

        // Play a triumphant ascending arpeggio
        const notes = [523.25, 659.25, 783.99]; // C5, E5, G5
        const duration = 0.4;

        notes.forEach((freq, index) => {
            const oscillator = this.audioContext!.createOscillator();
            const gainNode = this.audioContext!.createGain();

            oscillator.connect(gainNode);
            gainNode.connect(this.audioContext!.destination);

            oscillator.frequency.value = freq;
            oscillator.type = 'sine';

            const startTime = this.audioContext!.currentTime + (index * 0.15);
            gainNode.gain.setValueAtTime(0.3, startTime);
            gainNode.gain.exponentialRampToValueAtTime(0.01, startTime + duration);

            oscillator.start(startTime);
            oscillator.stop(startTime + duration);
        });
    }

    /**
     * Play check sound (king in check)
     */
    playCheck() {
        if (!this.enabled) return;
        this.ensureAudioContext();
        if (!this.audioContext) return;

        const duration = 0.15;
        const oscillator = this.audioContext.createOscillator();
        const gainNode = this.audioContext.createGain();

        oscillator.connect(gainNode);
        gainNode.connect(this.audioContext.destination);

        oscillator.frequency.value = 880; // A5 (high, alarming)
        oscillator.type = 'square';

        gainNode.gain.setValueAtTime(0.35, this.audioContext.currentTime);
        gainNode.gain.exponentialRampToValueAtTime(0.01, this.audioContext.currentTime + duration);

        oscillator.start(this.audioContext.currentTime);
        oscillator.stop(this.audioContext.currentTime + duration);
    }
}

// Export singleton instance
export const chessSounds = new ChessSounds();

// React hook for managing sound state with localStorage
export const useChessSound = (): [boolean, (enabled: boolean) => void] => {
    const [isSoundEnabled, setIsSoundEnabled] = useState(() => {
        if (typeof window === 'undefined') return true;
        const saved = localStorage.getItem('chessSoundEnabled');
        return saved !== null ? saved === 'true' : true;
    });

    useEffect(() => {
        chessSounds.setEnabled(isSoundEnabled);
    }, [isSoundEnabled]);

    const setSoundEnabled = (enabled: boolean) => {
        setIsSoundEnabled(enabled);
        if (typeof window !== 'undefined') {
            localStorage.setItem('chessSoundEnabled', String(enabled));
        }
    };

    return [isSoundEnabled, setSoundEnabled];
};
